"use client"

import React, { useActionState, useEffect, useState } from "react"
import Form from "next/form"
import { redirect } from "next/navigation"
import { B2CSignUpActionState } from "@/actions/authActions"
import { getApiToken, mergeCurrentBasketAction } from "@/actions/basketActions"
import { subscribeNewsletter } from "@/actions/subscriptionAction"
import { UB_LOGIN_REDIRECT_URL_COOKIE_NAME, UB_LOGIN_REDIRECT_URL_DEFAULT } from "@/constants"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons"
import Cookies from "js-cookie"
import { LoaderCircle } from "lucide-react"
import { useTranslations } from "next-intl"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { Controller, useForm } from "react-hook-form"

import { login } from "@/lib/auth-utils"
import { hasEntries } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { cn } from "@/lib/utils"
import { Button, Input, Label, RadioGroup, RadioGroupItem } from "@/components/ui/components"
import { PasswordToggleInput } from "@/components/ui/components/password-toggle-input"
import LegalConsentCheckbox from "@/components/auth/signup/LegalConsentCheckbox"
import NewsletterCheckbox from "@/components/auth/signup/NewsletterCheckbox"
import PasswordChecklist from "@/components/common/PasswordChecklist"
import TooltipClient from "@/components/common/TooltipClient"
import { UbB2CSignupFormStoryblok } from "@/components/storyblok/component-types"
import UbRichText from "@/components/storyblok/UbRichText"

import { B2CSignUpForm, B2CSignUpSchema } from "./signup"

const resolver = zodResolver(B2CSignUpSchema)

export const B2CSignUpFormClient = ({
  blok,
  action,
  values,
  sbLocale,
}: {
  blok: UbB2CSignupFormStoryblok
  action: (initialState: B2CSignUpActionState, formData: FormData) => Promise<B2CSignUpActionState>
  values: B2CSignUpForm
  sbLocale: string
}) => {
  const t = useTranslations("storefront")

  const redirectUrl =
    Cookies.get(UB_LOGIN_REDIRECT_URL_COOKIE_NAME) ??
    getLinkWithPrefixFromURLAndLocale(UB_LOGIN_REDIRECT_URL_DEFAULT, sbLocale)

  const { basketData, setBasketData } = useUbStorefrontState()

  const [password, setPassword] = useState("")

  const [passwordChecklistValidIconColor, setPasswordChecklistValidIconColor] = useState("text-gray-400")
  const [passwordChecklistInvalidIconColor, setPasswordChecklistInvalidIconColor] = useState("text-gray-400")

  useEffect(() => {
    setPasswordChecklistValidIconColor(password ? "text-green-500" : "text-gray-400")
    setPasswordChecklistInvalidIconColor(password ? "text-red-500" : "text-gray-400")
  }, [password])

  const [showLegalConsentRequired, setShowLegalConsentRequired] = useState<boolean>(false)

  const onLegalConsentCheckboxChange = (event) => {
    const checked = event.target.checked
    setShowLegalConsentRequired(!checked)
  }

  function handleNewsletter(formData: FormData) {
    if (formData.get("newsletter")) {
      subscribeNewsletter().then()
    }
  }

  const { executeRecaptcha } = useGoogleReCaptcha()

  async function withCaptcha(formData: FormData) {
    if (executeRecaptcha) {
      const recaptchaAction = "register"
      const recaptchaToken = await executeRecaptcha(recaptchaAction)
      formData.set("recaptcha_token", recaptchaToken)
      formData.set("action", recaptchaAction)
    }
    return formData
  }

  async function formActionWithCaptcha(initialState: any, formData: FormData) {
    const formDataWithCaptcha = await withCaptcha(formData)
    return action(initialState, formDataWithCaptcha).then(async (state) => {
      if (!hasEntries(state.errors)) {
        const username = formData.get("email") as string
        const password = formData.get("password") as string
        const anonymousApiToken = await getApiToken()
        const loginSuccessful = await login(username, password)
        if (loginSuccessful) {
          mergeCurrentBasketAction(basketData?.data?.id, anonymousApiToken).then((basketData) => {
            setBasketData(basketData)
          })
          handleNewsletter(formData)
          redirect(redirectUrl)
        } else {
          state.errors["root"] = { message: "Login failed" }
        }
      }
      return {
        values: state.values,
        errors: state.errors,
      }
    })
  }

  const [state, formAction, isPending] = useActionState(formActionWithCaptcha, {
    values,
    errors: {},
  })

  const { formState, register, reset, control } = useForm({
    errors: state.errors,
    mode: "onChange",
    resolver,
  })

  useEffect(() => {
    reset(state.values, {
      keepErrors: true,
    })
    if (state.errors.legalConsent) {
      setShowLegalConsentRequired(true)
    }
  }, [reset, state.values, state.errors.legalConsent])

  return (
    <Form className={"flex flex-col gap-4"} action={formAction}>
      <div className={"grid grid-cols-1 gap-x-8 md:grid-cols-12"}>
        <div className={"col-span-12 flex flex-col gap-4 md:col-span-6"}>
          <h2 className={"text-xl font-bold"}>{blok.label_contact_data ?? "Kontaktangaben"}</h2>
          <div>
            <Label className={"pb-1 font-bold"}>{blok.label_title}</Label>
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  name={field.name}
                  className={cn(
                    "flex flex-row justify-between gap-1 rounded-lg border  border-gray-300 bg-white px-5 py-3",
                    { "border-primary": formState.errors.title }
                  )}
                >
                  <div className="flex  grow items-center space-x-2    text-base outline-none ring-0 hover:border-black">
                    <RadioGroupItem value={t("salutation.ms")} id="r1" />
                    <Label className={"font-bold"} htmlFor="r1">
                      {blok.label_title_female ?? ""}
                    </Label>
                  </div>
                  <div className="flex  grow items-center space-x-2   text-base outline-none ring-0 hover:border-black">
                    <RadioGroupItem value={t("salutation.mr")} id="r2" />
                    <Label className={"font-bold"} htmlFor="r2">
                      {blok.label_title_male ?? ""}
                    </Label>
                  </div>
                  <div className="flex  grow items-center space-x-2  text-base outline-none ring-0 hover:border-black">
                    <RadioGroupItem value={t("salutation.divers")} id="r3" />
                    <Label className={"font-bold"} htmlFor="r3">
                      {blok.label_title_divers ?? ""}
                    </Label>
                  </div>
                </RadioGroup>
              )}
            />
            {formState.errors.title ? (
              <p className={"text-sm text-primary"} role="alert">
                {blok.invalid_title}
              </p>
            ) : null}
          </div>

          <div className={"flex w-full flex-row gap-2"}>
            <div className={"w-full"}>
              <Label className={"min-h-4 pb-1 font-bold"}>{blok.label_firstname}</Label>
              <Input {...register("firstName")} className={cn("", { "border-primary": formState.errors.firstName })} />
              {formState.errors.firstName ? (
                <p className={"text-sm text-primary"} role="alert">
                  {blok.invalid_firstname}
                </p>
              ) : null}
            </div>

            <div className={"w-full"}>
              <Label className={"min-h-4 pb-1 font-bold"}>{blok.label_lastname}</Label>
              <Input {...register("lastName")} className={cn("", { "border-primary": formState.errors.lastName })} />
              {formState.errors.lastName ? (
                <p className={"text-sm text-primary"} role="alert">
                  {blok.invalid_lastname}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className={"col-span-12 flex flex-col gap-4 md:col-span-6"}>
          <h2 className={"text-xl font-bold"}>{blok.label_login_data ?? "Login Informationen"}</h2>
          <div>
            <Label className={"pb-1 font-bold"}>{blok.label_email}</Label>
            <Input
              {...register("email")}
              autoComplete={"username"}
              className={cn("", { "border-primary": formState.errors.email })}
            />
            {formState.errors.email ? (
              <p className={"text-sm text-primary"} role="alert">
                {formState.errors.email.type === "notUnique" ? blok.existing_email : blok.invalid_email}
              </p>
            ) : null}
          </div>

          <div>
            <div className={"mt-2 flex min-h-4 flex-row gap-1"}>
              <Label className={"font-bold"}>{blok.label_password}</Label>
              {blok.tooltip_password?.[0] && <TooltipClient blok={blok.tooltip_password[0]} />}
            </div>
            <PasswordToggleInput
              className={cn("", { "border-primary": formState.errors.password })}
              {...register("password")}
              autoComplete={"new-password"}
              onChange={(e) => setPassword(e.target.value)}
            />
            {formState.errors.password ? (
              <p className={"text-sm text-primary"} role="alert">
                {blok.invalid_password}
              </p>
            ) : null}
            <PasswordChecklist
              className={"mt-1 flex flex-wrap items-center justify-start gap-2 text-xs"}
              itemClassName={"flex gap-1 items-center pt-0"}
              rules={["minLength", "lowercase", "capital", "specialChar", "number"]}
              value={password}
              minLength={10}
              messages={{
                minLength: blok.password_checklist_minlength,
                lowercase: blok.password_checklist_lowercase,
                capital: blok.password_checklist_capital,
                specialChar: blok.password_checklist_specialchar,
                number: blok.password_checklist_number,
              }}
              iconSize={16}
              iconComponents={{
                ValidIcon: <CheckCircledIcon className={passwordChecklistValidIconColor} />,
                InvalidIcon: <CrossCircledIcon className={passwordChecklistInvalidIconColor} />,
              }}
            />
          </div>
        </div>
      </div>

      {blok.recaptcha_v3_info_text && (
        <div className={"text-xs"}>
          <UbRichText blok={{ content: blok.recaptcha_v3_info_text, _uid: "", component: "ub_rich_text" }} />
        </div>
      )}

      <LegalConsentCheckbox
        onChange={onLegalConsentCheckboxChange}
        text={blok.label_legal_consent}
        showRequired={showLegalConsentRequired}
        requiredText={blok.missing_legal_consent}
      />

      <NewsletterCheckbox text={blok.label_newsletter} />

      <div className={"flex flex-row justify-center gap-4"}>
        <Button type={"submit"} size={"default"} disabled={isPending}>
          {!isPending ? <p>{blok.label_submit}</p> : <LoaderCircle className={"animate-spin"} />}
        </Button>
      </div>
    </Form>
  )
}
